/**
 * /admin/reminder-campaigns — لوحة موحَّدة لإدارة كلّ حملات التذكير (بخصم أو بدونه)
 * (المفضّلة، السلّة المهجورة، المناسبات) بخطوات متعدّدة قابلة للتخصيص الكامل.
 */
import * as React from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "@/lib/toast";
import { Loader2, Plus, Pencil, Heart, ShoppingCart, CalendarHeart, Sparkles, TrendingUp, BarChart3, ListChecks } from "lucide-react";
import { useAdminGate } from "@/hooks/useAdminGate";
import { useLocale } from "@/contexts/LocaleContext";
import { useCouponCampaigns } from "@/hooks/useCouponCampaigns";
import { setCampaignEnabled, type Campaign, type CampaignStep, type RewardKind } from "@/lib/admin/reminderCampaigns.functions";
import { getCampaignAnalytics, type CampaignAnalyticsRow } from "@/utils/campaignAnalytics.functions";
import { SiteShell } from "@/components/SiteShell";
import { AdminNav } from "@/components/admin/AdminNav";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { CouponStepEditor } from "@/components/admin/CouponStepEditor";
import { OccasionReminderSettingsPanel } from "@/components/admin/OccasionReminderSettingsPanel";

type ChannelTab = "all" | "whatsapp" | "email";

export const Route = createFileRoute("/admin/reminder-campaigns")({
  component: CouponsEnginePage,
  validateSearch: (search: Record<string, unknown>): { tab: ChannelTab } => {
    const t = search.tab;
    return { tab: t === "whatsapp" || t === "email" ? t : "all" };
  },
  head: () => ({
    meta: [
      { title: "Reminder Campaigns · Lecmo Admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
});

const CAMPAIGN_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  wishlist: Heart,
  abandoned_cart: ShoppingCart,
  occasion: CalendarHeart,
  tier_progress: TrendingUp,
  tier_progress_ambassador: TrendingUp,
};

function rewardLabel(s: CampaignStep, isAr: boolean): string {
  switch (s.reward_kind) {
    case "none": return isAr ? "بدون خصم" : "No discount";
    case "percent": return isAr ? `خصم ${s.reward_percent ?? 0}%` : `${s.reward_percent ?? 0}% off`;
    case "fixed_amount": {
      const amounts = Object.entries(s.reward_amount_per_market)
        .map(([k, v]) => `${v} ${k}`).join(" · ");
      return amounts || (isAr ? "مبلغ ثابت" : "Fixed amount");
    }
    case "free_shipping": return isAr ? "شحن مجّاني" : "Free shipping";
    case "gift_product": return isAr ? `هديّة: ${s.gift_product_handle ?? "—"}` : `Gift: ${s.gift_product_handle ?? "—"}`;
  }
}

function fmtRelative(iso: string | null, isAr: boolean): string {
  if (!iso) return isAr ? "لم تُرسَل بعد" : "Never";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return isAr ? "الآن" : "Just now";
  if (m < 60) return isAr ? `قبل ${m} د` : `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return isAr ? `قبل ${h} س` : `${h}h ago`;
  const d = Math.floor(h / 24);
  return isAr ? `قبل ${d} يوم` : `${d}d ago`;
}

function CampaignCard({ campaign, isAr, stats }: { campaign: Campaign; isAr: boolean; stats: CampaignAnalyticsRow | undefined }) {
  const qc = useQueryClient();
  const toggleFn = useServerFn(setCampaignEnabled);
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editingStep, setEditingStep] = React.useState<CampaignStep | null>(null);

  const Icon = CAMPAIGN_ICONS[campaign.campaign_key] ?? Sparkles;

  const toggleMut = useMutation({
    mutationFn: (v: boolean) => toggleFn({ data: { campaignId: campaign.id, isEnabled: v } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-coupon-campaigns"] });
      toast.success(isAr ? "تمّ" : "Saved");
    },
    onError: (e: any) => toast.error(e?.message ?? "Error"),
  });

  const openNew = () => { setEditingStep(null); setEditorOpen(true); };
  const openEdit = (s: CampaignStep) => { setEditingStep(s); setEditorOpen(true); };

  return (
    <>
      <div className="rounded-none border bg-card p-5 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Icon className="h-5 w-5 text-primary" />
            <div>
              <h2 className="text-base font-medium">
                {isAr ? campaign.name_ar : campaign.name_en}
              </h2>
              <p className="text-xs text-muted-foreground">
                {campaign.steps.length} {isAr ? "خطوة" : "step(s)"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {campaign.is_enabled ? (isAr ? "مُفعَّل" : "Enabled") : (isAr ? "موقوف" : "Disabled")}
            </span>
            <Switch
              checked={campaign.is_enabled}
              onCheckedChange={(v) => toggleMut.mutate(v)}
              disabled={toggleMut.isPending}
            />
          </div>
        </div>

        {/* KPIs + روابط (آخر ٣٠ يوم) */}
        <div className="flex flex-wrap items-center gap-2 rounded-none bg-muted/40 px-3 py-2">
          <span className="text-[11px] text-muted-foreground me-1">
            {isAr ? "آخر ٣٠ يوم:" : "Last 30d:"}
          </span>
          <Badge variant="secondary" className="font-mono">
            {stats?.sent ?? 0} {isAr ? "نجحت" : "sent"}
          </Badge>
          {(stats?.failed ?? 0) > 0 && (
            <Badge variant="destructive" className="font-mono">
              {stats?.failed} {isAr ? "فشلت" : "failed"}
            </Badge>
          )}
          <Badge variant="outline" className="font-mono">
            {stats?.unique_clicks ?? 0} {isAr ? "نقرة" : "clicks"}
          </Badge>
          {(() => {
            const sent = stats?.sent ?? 0;
            const clicks = stats?.unique_clicks ?? 0;
            if (sent === 0) return null;
            const ctr = (clicks / sent) * 100;
            const tone: "default" | "secondary" | "outline" = ctr >= 10 ? "default" : ctr >= 3 ? "secondary" : "outline";
            return (
              <Badge variant={tone} className="font-mono" title={isAr ? "معدّل النقر = نقرات فريدة ÷ رسائل ناجحة" : "CTR = unique clicks ÷ sent"}>
                CTR {ctr.toFixed(1)}%
              </Badge>
            );
          })()}
          <span className="text-[11px] text-muted-foreground">
            · {fmtRelative(stats?.last_sent_at ?? null, isAr)}
          </span>
          <div className="ms-auto flex items-center gap-1">
            <Button asChild variant="ghost" size="sm" className="h-7 px-2 text-xs">
              <Link to="/admin/campaign-analytics">
                <BarChart3 className="h-3.5 w-3.5 me-1" />
                {isAr ? "تحليلات" : "Analytics"}
              </Link>
            </Button>
            <Button asChild variant="ghost" size="sm" className="h-7 px-2 text-xs">
              <Link to="/admin/campaign-sends" search={{ campaign: campaign.campaign_key as any }}>
                <ListChecks className="h-3.5 w-3.5 me-1" />
                {isAr ? "السجلّ" : "Log"}
              </Link>
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          {campaign.steps.length === 0 && (
            <p className="text-sm text-muted-foreground py-2">
              {isAr ? "لا خطوات بعد. أضف خطوة لتبدأ." : "No steps yet. Add one to start."}
            </p>
          )}
          {campaign.steps.map((s) => (
            <button
              key={s.id}
              onClick={() => openEdit(s)}
              className="w-full flex items-center gap-3 rounded-none border bg-background hover:bg-accent/40 transition px-3 py-2.5 text-start"
            >
              <Badge variant="outline" className="shrink-0">
                {isAr ? `خطوة ${s.step_order}` : `Step ${s.step_order}`}
              </Badge>
              <div className="flex-1 min-w-0">
                <div className="text-sm">
                  {campaign.campaign_key === "occasion"
                    ? isAr
                      ? `${s.delay_days === 0 ? "يوم المناسبة" : `قبل ${s.delay_days} يوم`} · ${rewardLabel(s, true)}`
                      : `${s.delay_days === 0 ? "On the day" : `${s.delay_days}d before`} · ${rewardLabel(s, false)}`
                    : campaign.campaign_key === "tier_progress"
                    ? isAr
                      ? `حدّ التذكير: ${s.delay_days} طلب متبقٍّ`
                      : `Trigger: ≤ ${s.delay_days} order(s) left`
                    : isAr
                      ? `بعد ${s.delay_days} يوم · ${rewardLabel(s, true)}`
                      : `After ${s.delay_days}d · ${rewardLabel(s, false)}`}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {s.channel === "both" ? (isAr ? "إيميل + واتساب" : "Email + WhatsApp")
                    : s.channel === "email" ? (isAr ? "إيميل" : "Email")
                    : (isAr ? "واتساب" : "WhatsApp")}
                  {!s.is_active && ` · ${isAr ? "موقوفة" : "Inactive"}`}
                </div>
              </div>
              <Pencil className="h-4 w-4 text-muted-foreground shrink-0" />
            </button>
          ))}
        </div>

        <Button variant="outline" size="sm" onClick={openNew} className="w-full">
          <Plus className="h-4 w-4 me-2" />
          {isAr ? "أضف خطوة" : "Add step"}
        </Button>
      </div>

      <CouponStepEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        campaignId={campaign.id}
        step={editingStep}
        isAr={isAr}
        steps={campaign.steps}
        onSelectStep={setEditingStep}
        purpose={campaign.campaign_key as "wishlist" | "abandoned_cart" | "occasion" | "tier_progress" | "tier_progress_ambassador"}
      />
    </>
  );
}

function CouponsEnginePage() {
  const { ready, loading } = useAdminGate();
  const { locale } = useLocale();
  const isAr = locale === "ar";
  const { data: campaigns, isLoading } = useCouponCampaigns();
  const { tab } = Route.useSearch();
  const navigate = useNavigate({ from: "/admin/reminder-campaigns" });

  const analyticsFn = useServerFn(getCampaignAnalytics);
  const { data: analytics } = useQuery({
    queryKey: ["admin-campaign-analytics-inline", 30],
    queryFn: () => analyticsFn({ data: { rangeDays: 30 } }),
    enabled: ready,
    staleTime: 60_000,
  });
  const statsByCampaign = React.useMemo(() => {
    const m = new Map<string, CampaignAnalyticsRow>();
    for (const r of analytics?.campaigns ?? []) m.set(r.campaign_id, r);
    return m;
  }, [analytics]);

  if (loading || isLoading) {
    return (
      <SiteShell>
        <AdminNav />
        <div className="mx-auto max-w-3xl px-4 py-12 text-center">
          <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </SiteShell>
    );
  }

  if (!ready) {
    return (
      <SiteShell>
        <AdminNav />
        <div className="mx-auto max-w-3xl px-4 py-12 text-center text-muted-foreground">
          {isAr ? "صلاحيّة الأدمن مطلوبة." : "Admin only."}
        </div>
      </SiteShell>
    );
  }

  const matchesTab = (c: Campaign): boolean => {
    if (tab === "all") return true;
    return c.steps.some((s) =>
      s.channel === "both" || s.channel === tab
    );
  };

  const filtered = (campaigns ?? []).filter(matchesTab);

  const tabs: { key: ChannelTab; labelAr: string; labelEn: string }[] = [
    { key: "all", labelAr: "الكلّ", labelEn: "All" },
    { key: "whatsapp", labelAr: "واتساب", labelEn: "WhatsApp" },
    { key: "email", labelAr: "إيميل", labelEn: "Email" },
  ];

  return (
    <SiteShell>
      <AdminNav />
      <div className="mx-auto max-w-3xl px-4 py-8 space-y-5">
        <header className="space-y-1">
          <h1 className="text-xl font-medium">
            {isAr ? "رسائل المتابعة" : "Follow-up Messages"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isAr
              ? "كلّ رسائل المتابعة في مكان واحد. تحكّم كامل لكلّ خطوة: نوع الحافز، التوقيت، القناة، النصّ."
              : "All follow-up messages in one place. Full control per step: incentive type, timing, channel, text."}
          </p>
          <Link to="/admin/seasonal-campaigns" className="inline-flex items-center gap-2 text-sm text-primary hover:underline mt-2">
            <CalendarHeart className="h-4 w-4" />
            {isAr ? "حملات موسميّة (عيد الأمّ، الحبّ، الوطنيّ...)" : "Seasonal broadcasts (Mother's Day, Valentine's, National Day...)"}
          </Link>
        </header>

        <div className="flex items-center gap-2 border-b">
          {tabs.map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => navigate({ search: { tab: t.key } })}
                className={`px-3 py-2 text-sm border-b-2 -mb-px transition ${
                  active
                    ? "border-primary text-foreground font-medium"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {isAr ? t.labelAr : t.labelEn}
              </button>
            );
          })}
        </div>

        {filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            {isAr ? "لا حملات بهذه القناة." : "No campaigns for this channel."}
          </p>
        ) : (
          filtered.map((c) => (
            <CampaignCard key={c.id} campaign={c} isAr={isAr} stats={statsByCampaign.get(c.id)} />
          ))
        )}

        {/* إعدادات تذكير المناسبات (نوافذ التذكير قبل المناسبة) */}
        <details className="mt-6 rounded-none border border-border bg-card">
          <summary className="cursor-pointer px-4 py-3 text-sm font-medium hover:bg-muted/50">
            {isAr ? "إعدادات تذكير المناسبات" : "Occasion reminder settings"}
          </summary>
          <div className="border-t border-border p-4">
            <OccasionReminderSettingsPanel />
          </div>
        </details>
      </div>
    </SiteShell>
  );
}
